import { getCurrentChapter } from "@/config/chapters";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "motion/react";
import { PageHeader } from "@/components/page-header";
import { SiteFooter } from "@/components/site-footer";

export const Route = createFileRoute("/how-it-works")({
  head: () => {
    const title = "How the House Works · House of Apocrypha";
    const description =
      "Four chapters a year. Sample first, pre-order the bottle, open only when certain. Read how the House of Apocrypha chapter model works.";
    const url = "https://houseofapocrypha.com/how-it-works";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://houseofapocrypha.com/" },
              { "@type": "ListItem", position: 2, name: "How It Works", item: url },
            ],
          }),
        },
      ],
    };
  },
  component: HowItWorksPage,
});

const fade = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" } as const,
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] as const },
};

const quickSteps = [
  "Chapter Opens",
  "Vials Ship",
  "Bottle Pre-Orders",
  "Chapter Closes",
  "Orders Ship",
];

const timeline = [
  {
    title: "Chapter Opens",
    body: "The current fragrances are revealed. Chapter Vials and bottle pre-orders become available the same day.",
  },
  {
    title: "Vials Ship Weekly",
    body: "Chapter Vials dispatch every Friday throughout the open window, so you have time to test before deciding on a bottle.",
  },
  {
    title: "The Window Closes",
    body: "Bottle pre-orders close, usually after several weeks — sooner if the chapter reaches capacity. The chapter is sealed.",
  },
  {
    title: "The Work Continues",
    body: "Fragrances are blended, rested, filtered, bottled, numbered, and prepared in the studio.",
  },
  {
    title: "Orders Ship",
    body: "Bottles leave with their companion vials, in the estimated dispatch window shown on each product page.",
  },
  {
    title: "The Chapter Enters the Library",
    body: "Every fragrance keeps its record. Some return. Some are revised. Some remain closed.",
  },
];

const wheel = [
  { season: "Spring", month: "March" },
  { season: "Summer", month: "June" },
  { season: "Autumn", month: "September" },
  { season: "Winter", month: "December" },
];

function Section({
  eyebrow,
  heading,
  children,
}: {
  eyebrow?: string;
  heading: string;
  children: React.ReactNode;
}) {
  return (
    <motion.section {...fade} className="mx-auto max-w-3xl px-6 py-12 sm:py-16">
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}
      <h2 className="mt-4 text-3xl text-parchment sm:text-4xl">{heading}</h2>
      <div className="mt-8 space-y-5 text-lg leading-relaxed text-foreground/85">
        {children}
      </div>
    </motion.section>
  );
}

function SeasonalWheel() {
  return (
    <div className="mx-auto mt-10 grid max-w-md grid-cols-2 gap-px border border-gold/30 bg-gold/20 sm:grid-cols-4">
      {wheel.map((w) => (
        <div
          key={w.season}
          className="flex flex-col items-center bg-background px-4 py-6 text-center"
        >
          <span className="font-display text-[0.6rem] uppercase tracking-[0.28em] text-gold">
            {w.season}
          </span>
          <span className="mt-3 font-serif text-xl text-parchment">{w.month}</span>
        </div>
      ))}
    </div>
  );
}

function VialsVsBottles() {
  const rows: [string, string, string][] = [
    ["Size", "Sample", "Full bottle"],
    ["Dispatch", "Ships every Friday", "After the chapter closes"],
    ["Purpose", "For testing", "For committing"],
    ["Availability", "Only while the chapter is open", "Limited to chapter capacity"],
  ];
  return (
    <div className="mx-auto mt-2 max-w-3xl border border-gold/30">
      <div className="grid grid-cols-[1.1fr_1fr_1fr] border-b border-gold/20 bg-gold/[0.04]">
        <div className="px-5 py-4 font-display text-[0.6rem] uppercase tracking-[0.24em] text-muted-foreground">
          &nbsp;
        </div>
        <div className="px-5 py-4 font-display text-[0.62rem] uppercase tracking-[0.24em] text-gold">
          Chapter Vials
        </div>
        <div className="px-5 py-4 font-display text-[0.62rem] uppercase tracking-[0.24em] text-gold">
          Bottle Pre-Orders
        </div>
      </div>
      {rows.map(([label, vial, bottle], i) => (
        <div
          key={label}
          className={`grid grid-cols-[1.1fr_1fr_1fr] ${
            i < rows.length - 1 ? "border-b border-gold/15" : ""
          }`}
        >
          <div className="px-5 py-4 font-display text-[0.62rem] uppercase tracking-[0.22em] text-muted-foreground">
            {label}
          </div>
          <div className="px-5 py-4 text-base text-parchment/90">{vial}</div>
          <div className="px-5 py-4 text-base text-parchment/90">{bottle}</div>
        </div>
      ))}
    </div>
  );
}

function VialPromiseCard() {
  return (
    <div className="mx-auto max-w-2xl border border-gold/50 bg-gold/[0.04] p-8 sm:p-10">
      <div className="flex items-center justify-between">
        <span className="font-display text-[0.62rem] uppercase tracking-[0.32em] text-gold">
          The Vial Promise
        </span>
        <span className="font-display text-gold/70">✶</span>
      </div>
      <p className="mt-6 font-serif text-xl leading-relaxed text-parchment sm:text-2xl">
        Every bottle includes a companion vial of the same fragrance.
      </p>
      <ol className="mt-6 space-y-3 text-base text-foreground/85">
        <li className="flex gap-4">
          <span className="font-display text-[0.7rem] text-gold">I.</span>
          Wear the vial first.
        </li>
        <li className="flex gap-4">
          <span className="font-display text-[0.7rem] text-gold">II.</span>
          Decide over three days — different hours, different moods.
        </li>
        <li className="flex gap-4">
          <span className="font-display text-[0.7rem] text-gold">III.</span>
          Open the bottle only when you are certain.
        </li>
      </ol>
      <div className="mt-7 border-t border-gold/20 pt-5 text-sm leading-relaxed text-muted-foreground">
        If the chapter is not to your liking, contact us within 14 days of
        delivery. Return the unopened, sealed bottle in its original packaging —
        the companion vial remains with you. Once received, we refund the
        bottle order per our{" "}
        <Link to="/faq" className="text-gold underline-offset-4 hover:underline">
          Shipping & Returns policy
        </Link>
        .
      </div>
    </div>
  );
}

function LibraryLedger() {
  const rows: [string, string, string][] = [
    ["Spring 2026", "In the Library", "First edition"],
    ["Summer 2026", "In the Library", "First edition"],
    ["Fall 2026", "Open", "Current chapter — dispatches third week of September"],
    ["Winter 2026", "Forthcoming", "Opens third week of December"],
  ];
  return (
    <div className="mx-auto mt-2 max-w-2xl border border-gold/30">
      <div className="grid grid-cols-[1fr_1fr_1.2fr] border-b border-gold/20 bg-gold/[0.04]">
        {["Chapter", "Status", "Notes"].map((h) => (
          <div
            key={h}
            className="px-5 py-3 font-display text-[0.6rem] uppercase tracking-[0.24em] text-gold"
          >
            {h}
          </div>
        ))}
      </div>
      {rows.map(([c, s, n], i) => (
        <div
          key={c}
          className={`grid grid-cols-[1fr_1fr_1.2fr] ${
            i < rows.length - 1 ? "border-b border-gold/15" : ""
          }`}
        >
          <div className="px-5 py-4 font-serif text-parchment">{c}</div>
          <div className="px-5 py-4 text-sm text-parchment/85">{s}</div>
          <div className="px-5 py-4 text-sm text-muted-foreground">{n}</div>
        </div>
      ))}
    </div>
  );
}

function HowItWorksPage() {
  return (
    <div className="min-h-screen bg-background">
      <PageHeader />

      <main>
        <nav className="mx-auto max-w-7xl px-6 pt-8 text-sm" aria-label="Breadcrumb">
          <Link to="/" className="text-muted-foreground transition-colors hover:text-gold">
            Home
          </Link>
          <span className="mx-2 text-muted-foreground/50">/</span>
          <span className="text-parchment">How It Works</span>
        </nav>

        {/* Hero */}
        <header className="mx-auto max-w-3xl px-6 pt-10 pb-2 text-center">
          <p className="eyebrow">The Chapter Model</p>
          <h1 className="mt-5 font-display text-5xl text-gradient-gold sm:text-6xl">
            How the House Works
          </h1>
          <p className="mx-auto mt-6 max-w-xl font-serif text-lg leading-relaxed text-foreground/85 sm:text-xl">
            Natural perfumes, released in seasonal chapters. Sample first,
            pre-order the bottle, and open only when certain.
          </p>
          <div className="ornament mt-8">
            <span>✶</span>
          </div>
        </header>

        {/* At-a-glance process strip */}
        <section className="mx-auto max-w-5xl px-6 pb-4 pt-6">
          <p className="eyebrow text-center">At a Glance</p>
          <ol className="mt-6 grid grid-cols-2 gap-px border border-gold/30 bg-gold/20 sm:grid-cols-5">
            {quickSteps.map((step, i) => (
              <li
                key={step}
                className="flex flex-col items-center justify-center bg-background px-3 py-5 text-center"
              >
                <span className="font-display text-[0.6rem] uppercase tracking-[0.24em] text-gold">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="mt-2 font-serif text-sm text-parchment sm:text-base">
                  {step}
                </span>
              </li>
            ))}
          </ol>
        </section>

        <Section heading="The Seasonal Chapter">
          <p>
            Each year opens four chapters: Spring in March, Summer in June,
            Autumn in September, and Winter in December. Each opens in the
            third week of its month.
          </p>
          <p>
            A chapter gathers a small group of natural perfumes around a theme.
            Bottles are offered by pre-order for a limited window — usually
            several weeks, unless the chapter reaches capacity first.
          </p>
          <p>
            The House does not keep a permanent catalogue. There is only the
            current chapter, the archive, and the list that knows what opens
            next.
          </p>
          <SeasonalWheel />
        </Section>

        <Section heading="Chapter Vials">
          <p>
            Chapter Vials are 2 ml samples from the current chapter, available
            individually or as a complete set while the chapter is open.
          </p>
          <p>
            They exist for those who wish to experience a fragrance before
            ordering a bottle. They are not a permanent sample programme — when
            the chapter closes, the vials close with it.
          </p>
          <p className="font-serif italic text-gold/90">
            Vials ship every Friday throughout the chapter, so you have time to
            test before the bottle window closes.
          </p>
          {getCurrentChapter().vialsAvailable ? (
            <div className="pt-2">
              <Link
                to="/products/$slug"
                params={{ slug: "chapter-vials" }}
                className="group inline-flex items-center gap-3 border border-gold bg-gold px-7 py-3 font-display text-[0.7rem] uppercase tracking-[0.26em] text-ink transition-all duration-300 ease-out hover:bg-gold-soft hover:shadow-[0_0_20px_rgba(184,164,123,0.25)] active:scale-[0.97]"
              >
                Shop Chapter Vials
                <span className="transition-transform duration-500 group-hover:translate-x-1">→</span>
              </Link>
            </div>
          ) : (
            <p className="font-serif italic text-muted-foreground">
              Vials return when the next chapter opens.
            </p>
          )}

        </Section>

        <Section heading="Bottle Pre-Orders">
          <p>When a chapter opens, full bottles are available by pre-order.</p>
          <p>
            You choose the fragrance. You place your order. Payment is taken at
            checkout. Once the chapter closes, the work continues: blending,
            resting, filtering, bottling, and dispatch.
          </p>
          <p>
            The pre-order window is kept open long enough for vial customers to
            sample before choosing a bottle. The estimated dispatch window is
            shown on each product page and in your order confirmation.
          </p>
        </Section>

        {/* Vials vs Bottles */}
        <motion.section {...fade} className="mx-auto max-w-3xl px-6 py-12 sm:py-16">
          <p className="eyebrow">Side by Side</p>
          <h2 className="mt-4 text-3xl text-parchment sm:text-4xl">
            Vials &amp; Bottles
          </h2>
          <div className="mt-8">
            <VialsVsBottles />
          </div>
        </motion.section>

        {/* Vial Promise */}
        <motion.section {...fade} className="mx-auto max-w-3xl px-6 py-12 sm:py-16">
          <VialPromiseCard />
        </motion.section>

        {/* Library */}
        <motion.section {...fade} className="mx-auto max-w-3xl px-6 py-12 sm:py-16">
          <p className="eyebrow">The Library</p>
          <h2 className="mt-4 text-3xl text-parchment sm:text-4xl">A record of every chapter</h2>
          <div className="mt-8 space-y-5 text-lg leading-relaxed text-foreground/85">
            <p>
              Each chapter leaves a record. As the House grows, past fragrances,
              editions, and returns are kept in the Library — some return
              unchanged, some return as revised editions, some remain closed.
            </p>
            <p className="font-serif italic text-muted-foreground">
              The Library begins with the first chapter.
            </p>
          </div>
          <div className="mt-8">
            <LibraryLedger />
          </div>
        </motion.section>

        {/* Detailed Timeline */}
        <section className="mx-auto max-w-3xl px-6 py-12 sm:py-16">
          <p className="eyebrow text-center">The Full Timeline</p>
          <ol className="mt-12 space-y-10 border-l border-gold/30 pl-8">
            {timeline.map((step, i) => (
              <motion.li
                key={step.title}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                className="relative"
              >
                <span className="absolute -left-[2.4rem] flex h-6 w-6 items-center justify-center border border-gold/60 bg-background font-display text-[0.65rem] uppercase tracking-[0.18em] text-gold">
                  {i + 1}
                </span>
                <h3 className="font-display text-sm uppercase tracking-[0.24em] text-gold">
                  {step.title}
                </h3>
                <p className="mt-3 text-lg leading-relaxed text-foreground/85">{step.body}</p>
              </motion.li>
            ))}
          </ol>
        </section>

        {/* CTA */}
        <section className="mx-auto max-w-3xl px-6 py-16 text-center sm:py-24">
          <div className="ornament">
            <span>✶</span>
          </div>
          <p className="mt-10 text-lg leading-relaxed text-muted-foreground">
            Join the list to know when the next chapter opens. After each
            chapter closes, a letter goes out recording what entered the
            Library — what returned, what changed, and what may not return.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
            <Link
              to="/"
              hash="release-access"
              className="group inline-flex items-center justify-center gap-3 border border-gold bg-gold px-9 py-4 font-display text-xs uppercase tracking-[0.26em] text-ink transition-all duration-300 ease-out hover:bg-gold-soft hover:shadow-[0_0_20px_rgba(184,164,123,0.25)] active:scale-[0.97]"
            >
              Join the Release List
              <span className="transition-transform duration-500 group-hover:translate-x-1">→</span>
            </Link>
            <Link
              to="/products"
              className="group inline-flex items-center justify-center gap-3 border border-gold/60 bg-background/20 px-9 py-4 font-display text-xs uppercase tracking-[0.26em] text-parchment transition-colors duration-500 hover:border-gold hover:bg-gold/10"
            >
              Browse Open Chapters
              <span className="transition-transform duration-500 group-hover:translate-x-1">→</span>
            </Link>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
